import { useCallback, useEffect, useMemo, useState } from 'react'
import analyticalLibraryItems, {
  AnalyticalLibraryItem,
  analyticalLibraryThemes,
} from './analyticalLibraryItems'
import { useLocalStorage } from 'react-use'
import styled from 'styled-components'
import AppLayout from '~/components/appLayout'
import { trackAnalyticsEvent } from '~/utils/client/analytics'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faArrowDownAZ,
  faArrowDownZA,
  faArrowDownWideShort,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import theme, { institutionalColors } from '~/design'
import { ButtonVariants } from '~/components/buttonVariants'
import PageHeader from '~/components/pageHeader'
import ContextMenu from '~/prix/react/components/contextMenu/contextMenu'
import AnalyticalLibraryCard, {
  AnalyticalLibraryCardSkeleton,
} from './analyticalLibraryCard.component'
import { toastError, toastSuccess } from '~/components/toast'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import monitorUserReportPreferencesDefinition from '~/packages/monitor/userReportPreferences/monitorUserReportPreferences.definition'
import { Tab, TabList, Tabs } from '~/components/tabs'
import { useNavigate, useParams } from 'react-router-dom'

type AnalyticalLibrarySortOption = 'initial' | 'titleAscending' | 'titleDescending'

type MonitorUserReportPreference = {
  destinationPath: string
  isFixed: boolean
  ordinalPosition?: number
}

const shortcutPlaceholderImage = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360">
  <rect width="640" height="360" fill="#e0e5eb"/>
  <path d="M110 260L244 178L368 224L530 126V302H110V260Z" fill="#ffffff"/>
  <path d="M110 260L244 178L368 224L530 126" fill="none" stroke="#58c4da" stroke-width="18" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M330 78C330 50 352 28 380 28C408 28 430 50 430 78C430 120 380 172 380 172C380 172 330 120 330 78Z" fill="#0f438a"/>
  <circle cx="380" cy="78" r="18" fill="#ffffff"/>
  <circle cx="110" cy="260" r="14" fill="#0f438a"/>
  <circle cx="244" cy="178" r="14" fill="#0f438a"/>
  <circle cx="368" cy="224" r="14" fill="#0f438a"/>
  <circle cx="530" cy="126" r="14" fill="#0f438a"/>
</svg>
`)}`

const analyticalLibrarySortOptionStorageKey = 'analyticalLibrarySortOption'
const defaultAnalyticalLibrarySortOption: AnalyticalLibrarySortOption = 'initial'
const analyticalLibrarySortOptions: AnalyticalLibrarySortOption[] = [
  'initial',
  'titleAscending',
  'titleDescending',
]
const analyticalLibraryDestinationPathSet = new Set(
  analyticalLibraryItems.map(item => item.destinationPath),
)

function normalizeSearchText(text: string) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

function getAnalyticalLibrarySortOption(sortOption: AnalyticalLibrarySortOption | undefined) {
  return sortOption && analyticalLibrarySortOptions.includes(sortOption)
    ? sortOption
    : defaultAnalyticalLibrarySortOption
}

function getFixedAnalyticalLibraryDestinationPaths(preferences: MonitorUserReportPreference[]) {
  return preferences
    .filter(preference => preference.isFixed)
    .sort((firstPreference, secondPreference) => {
      const firstPosition = firstPreference.ordinalPosition ?? Number.MAX_SAFE_INTEGER
      const secondPosition = secondPreference.ordinalPosition ?? Number.MAX_SAFE_INTEGER

      if (firstPosition !== secondPosition) {
        return firstPosition - secondPosition
      }

      return firstPreference.destinationPath.localeCompare(
        secondPreference.destinationPath,
        'pt-BR',
      )
    })
    .map(preference => preference.destinationPath)
    .filter(destinationPath => analyticalLibraryDestinationPathSet.has(destinationPath))
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex: 1;
  }
`

const Input = styled.input`
  padding: 0.75rem;
  padding-left: ${({ theme }) => theme.padding(3)};
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  font-size: 16px;
  width: 100%;
  min-width: 270px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.darkGrey};
  }
`

const InputIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: ${({ theme }) => theme.padding(1)};
  z-index: 1;
  width: 18px;
  height: 18px;
`

const WrapperFilter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: stretch;
`

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  align-items: stretch;
  padding: 24px;
  flex-direction: column;
  gap: 24px;
`

const TabListStyled = styled(TabList)`
  max-width: 1790px;
  margin: 0 auto;
  width: 100%;
`

const Content = styled.main<{ $hasVisibleItems: boolean }>`
  flex: 1;
  width: 100%;
  max-width: 1790px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  align-content: ${({ $hasVisibleItems }) => ($hasVisibleItems ? 'start' : 'stretch')};
  gap: 24px;
  margin: 0 auto;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1160px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  @media (min-width: 1720px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: 32px;
  color: ${({ theme }) => theme.colors.darkGrey};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  h3 {
    font-size: 16px;
    font-weight: 500;
  }
`

function sortAnalyticalLibraryItems(
  items: AnalyticalLibraryItem[],
  sortOption: AnalyticalLibrarySortOption,
  fixedAnalyticalLibraryDestinationPaths: string[],
) {
  const sortedItems =
    sortOption === 'initial'
      ? items
      : [...items].sort((firstItem, secondItem) => {
          const comparison = firstItem.title.localeCompare(secondItem.title, 'pt-BR')

          return sortOption === 'titleAscending' ? comparison : -comparison
        })

  if (fixedAnalyticalLibraryDestinationPaths.length === 0) {
    return sortedItems
  }

  const fixedAnalyticalLibraryDestinationPathSet = new Set(fixedAnalyticalLibraryDestinationPaths)

  return [...sortedItems].sort((firstItem, secondItem) => {
    const isFirstItemFixed = fixedAnalyticalLibraryDestinationPathSet.has(firstItem.destinationPath)
    const isSecondItemFixed = fixedAnalyticalLibraryDestinationPathSet.has(
      secondItem.destinationPath,
    )

    if (isFirstItemFixed === isSecondItemFixed) {
      return 0
    }

    return isFirstItemFixed ? -1 : 1
  })
}

export default function AnalyticalLibraryScreen() {
  const navigate = useNavigate()
  const { slugTheme } = useParams()
  const [showFilter, setShowFilter] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [selectedTab, setSelectedTab] = useState(slugTheme ?? 'Todos')
  const [storedSortOption, setSortOption] = useLocalStorage<AnalyticalLibrarySortOption>(
    analyticalLibrarySortOptionStorageKey,
    defaultAnalyticalLibrarySortOption,
  )
  const sortOption = getAnalyticalLibrarySortOption(storedSortOption)
  const [fixedAnalyticalLibraryDestinationPaths, setFixedAnalyticalLibraryDestinationPaths] =
    useState<string[]>([])
  const [
    changingAnalyticalLibraryFixedStateDestinationPaths,
    setChangingAnalyticalLibraryFixedStateDestinationPaths,
  ] = useState<string[]>([])
  const [hasLoadedReportPreferences, setHasLoadedReportPreferences] = useState(false)
  const { callAction: callMonitorUserReportPreferences } = useActionFunction(
    monitorUserReportPreferencesDefinition,
    {
      initialIsLoading: false,
    },
  )

  const fixedAnalyticalLibraryDestinationPathSet = useMemo(
    () => new Set(fixedAnalyticalLibraryDestinationPaths),
    [fixedAnalyticalLibraryDestinationPaths],
  )

  const isLoading = !hasLoadedReportPreferences

  const loadAnalyticalLibraryPreferences = useCallback(async () => {
    try {
      const result = await callMonitorUserReportPreferences({}).resultPromise

      setFixedAnalyticalLibraryDestinationPaths(
        getFixedAnalyticalLibraryDestinationPaths(result.items),
      )
    } catch (error) {
      console.error(error)
      // toastError('Não foi possível carregar os relatórios e estudos fixados.')
    } finally {
      setHasLoadedReportPreferences(true)
    }
  }, [callMonitorUserReportPreferences])

  useEffect(() => {
    loadAnalyticalLibraryPreferences().catch(error => console.error(error))
  }, [loadAnalyticalLibraryPreferences])

  const changeAnalyticalLibraryFixedState = useCallback(
    async (destinationPath: string, isFixed: boolean) => {
      if (!analyticalLibraryDestinationPathSet.has(destinationPath)) {
        return
      }

      const previousFixedAnalyticalLibraryDestinationPaths = fixedAnalyticalLibraryDestinationPaths
      const nextFixedAnalyticalLibraryDestinationPaths = isFixed
        ? previousFixedAnalyticalLibraryDestinationPaths.includes(destinationPath)
          ? previousFixedAnalyticalLibraryDestinationPaths
          : [...previousFixedAnalyticalLibraryDestinationPaths, destinationPath]
        : previousFixedAnalyticalLibraryDestinationPaths.filter(
            currentDestinationPath => currentDestinationPath !== destinationPath,
          )

      setChangingAnalyticalLibraryFixedStateDestinationPaths(currentDestinationPaths =>
        currentDestinationPaths.includes(destinationPath)
          ? currentDestinationPaths
          : [...currentDestinationPaths, destinationPath],
      )

      try {
        const result = await callMonitorUserReportPreferences({
          preferences: [
            {
              destinationPath,
              isFixed,
              ordinalPosition: isFixed
                ? nextFixedAnalyticalLibraryDestinationPaths.indexOf(destinationPath)
                : undefined,
            },
          ],
        }).resultPromise

        setFixedAnalyticalLibraryDestinationPaths(
          getFixedAnalyticalLibraryDestinationPaths(result.items),
        )

        toastSuccess(`O relatório ou estudo foi ${isFixed ? 'fixado' : 'desafixado'} com sucesso.`)
      } catch (error) {
        console.error(error)
        setFixedAnalyticalLibraryDestinationPaths(previousFixedAnalyticalLibraryDestinationPaths)
        toastError(`Não foi possível ${isFixed ? 'fixar' : 'desafixar'} o relatório ou estudo.`)
      } finally {
        setChangingAnalyticalLibraryFixedStateDestinationPaths(currentDestinationPaths =>
          currentDestinationPaths.filter(
            currentDestinationPath => currentDestinationPath !== destinationPath,
          ),
        )
      }
    },
    [callMonitorUserReportPreferences, fixedAnalyticalLibraryDestinationPaths],
  )

  const visibleAnalyticalLibraryItems = useMemo(() => {
    const normalizedSearchText = normalizeSearchText(searchText.trim())
    const filteredItems =
      normalizedSearchText.length === 0
        ? analyticalLibraryItems
        : analyticalLibraryItems.filter(item => {
            const normalizedItemText = normalizeSearchText(
              `${item.title} ${analyticalLibraryThemes?.[item.themeSlug].name} ${item.productType} ${item.description} ${item.technology} ${item.navigationType}`,
            )

            return normalizedItemText.includes(normalizedSearchText)
          })

    return sortAnalyticalLibraryItems(
      filteredItems,
      sortOption,
      fixedAnalyticalLibraryDestinationPaths,
    )
  }, [fixedAnalyticalLibraryDestinationPaths, searchText, sortOption])

  const ListSkeleton = useMemo(() => {
    const skeletons = []

    for (let i = 0; i < analyticalLibraryItems.length; i++) {
      skeletons.push(<AnalyticalLibraryCardSkeleton key={`skeleton-${i}`} />)
    }

    return skeletons
  }, [analyticalLibraryItems])

  const tabsNames = useMemo(() => {
    const themes = new Set<string>()
    themes.add('Todos')
    analyticalLibraryItems.forEach(item => themes.add(item.themeSlug))
    return Array.from(themes)
  }, [analyticalLibraryItems])

  const filteredAnalyticalLibraryItemsBySelectedTab = useMemo(() => {
    if (selectedTab === 'Todos') {
      return visibleAnalyticalLibraryItems
    }

    return visibleAnalyticalLibraryItems.filter(item => item.themeSlug === selectedTab)
  }, [selectedTab, visibleAnalyticalLibraryItems])

  const handleTabChange = useCallback((tabName: string) => {
    setSelectedTab(tabName)

    if (tabName === 'Todos') {
      navigate('/app/analytical-library')
      return
    }

    navigate(`/app/analytical-library/${tabName}`)
  }, [])

  const handleCount = useCallback(
    (tabName: string) => {
      if (tabName === 'Todos') {
        return visibleAnalyticalLibraryItems.length
      }

      return visibleAnalyticalLibraryItems.filter(item => item.themeSlug === tabName).length
    },
    [visibleAnalyticalLibraryItems],
  )

  useEffect(() => {
    trackAnalyticsEvent('study_list_opened', {
      source_screen: 'study_list',
    })
  }, [])

  return (
    <AppLayout title='Biblioteca Analítica' dockActive='analytical-library'>
      <PageHeader
        title='Biblioteca Analítica'
        subtitle='Encontre abaixo relatórios e estudos para consulta na biblioteca analítica.'
      >
        <InputWrapper>
          <InputIcon icon={faSearch} color={theme.colors.darkGrey} />
          <Input
            aria-label='Pesquisar conteúdo'
            value={searchText}
            placeholder='Pesquisar conteúdo'
            onChange={event => setSearchText(event.target.value)}
          />
        </InputWrapper>
        <WrapperFilter>
          <ButtonVariants
            title='Ordenar'
            aria-label='Ordenar'
            icon={
              sortOption === 'titleAscending'
                ? faArrowDownAZ
                : sortOption === 'titleDescending'
                  ? faArrowDownZA
                  : faArrowDownWideShort
            }
            size='lg'
            backgroundColor='transparent'
            color={theme.colors.darkGrey}
            onClick={() => setShowFilter(previousShowFilter => !previousShowFilter)}
          />
          {showFilter && (
            <ContextMenu
              xBasis='right'
              yBasis='top'
              style={{ top: '100%' }}
              header='Ordenar por:'
              arrow={{ position: 'top', x: 200 }}
              onExit={() => setShowFilter(false)}
              functionalities={[
                {
                  run: () => setSortOption('initial'),
                  title: 'Ordem inicial',
                  icon: <FontAwesomeIcon icon={faArrowDownWideShort} />,
                },
                {
                  run: () => setSortOption('titleAscending'),
                  title: 'Ordem alfabética',
                  icon: <FontAwesomeIcon icon={faArrowDownAZ} />,
                },
                {
                  run: () => setSortOption('titleDescending'),
                  title: 'Ordem alfabética reversa',
                  icon: <FontAwesomeIcon icon={faArrowDownZA} />,
                },
              ]}
            />
          )}
        </WrapperFilter>
      </PageHeader>
      <Wrapper>
        {tabsNames.length > 0 && (
          <Tabs defaultTab={slugTheme ?? 'Todos'} onChange={handleTabChange}>
            <TabListStyled>
              {tabsNames.map(item => (
                <Tab
                  key={item}
                  id={item}
                  title={item === 'Todos' ? 'Todos' : analyticalLibraryThemes?.[item]?.name}
                  count={!!searchText && handleCount(item) > 0 ? handleCount(item) : undefined}
                />
              ))}
            </TabListStyled>
          </Tabs>
        )}
        <Content $hasVisibleItems={handleCount(selectedTab) > 0}>
          {isLoading ? (
            ListSkeleton
          ) : filteredAnalyticalLibraryItemsBySelectedTab.length > 0 ? (
            filteredAnalyticalLibraryItemsBySelectedTab.map(item => (
              <AnalyticalLibraryCard
                key={item.destinationPath}
                title={item.title}
                imageSource={item.imageSource ?? shortcutPlaceholderImage}
                imageAlternativeText={item.imageAlternativeText}
                description={item.description}
                destinationPath={item.destinationPath}
                tags={[
                  analyticalLibraryThemes?.[item.themeSlug] ?? [],
                  ...(item.redirect
                    ? [{ name: 'Link externo', color: institutionalColors.blueSebrae30 }]
                    : []),
                ].filter(Boolean)}
                redirect={item.redirect}
                isFixed={fixedAnalyticalLibraryDestinationPathSet.has(item.destinationPath)}
                isChangingFixedState={changingAnalyticalLibraryFixedStateDestinationPaths.includes(
                  item.destinationPath,
                )}
                onChangeFixedState={isFixed => {
                  void changeAnalyticalLibraryFixedState(item.destinationPath, isFixed)
                }}
              />
            ))
          ) : (
            <EmptyState>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                color={theme.colors.mediumGrey}
                size='5x'
              />
              <h3>
                Nenhum conteúdo encontrado{' '}
                {selectedTab === 'Todos'
                  ? 'na biblioteca analítica'
                  : `em ${analyticalLibraryThemes?.[selectedTab]?.name}`}
                .
              </h3>
            </EmptyState>
          )}
        </Content>
      </Wrapper>
    </AppLayout>
  )
}
